import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  forwardRef,
} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  CircularProgress,
  Box,
  Divider,
  Chip,
  Slide,
  useTheme,
  Tabs,
  Tab,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  getPaymentsBySalesIdDesc,
  getRemainingBalanceForSale,
  getNextPaymentDueForSale,
} from "../../services/PaymentService";
import LogPayments from "./LogPayments";
import { tokens } from "../../theme";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PaymentsDialog = ({ open, onClose, salesId, rowData }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [error, setError] = useState(null);
  const [remainingBalance, setRemainingBalance] = useState(null);
  const [backendNextDue, setBackendNextDue] = useState(null);

  const [currentTab, setCurrentTab] = useState(0);

  const fetchPaymentsAndBalance = useCallback(async () => {
    if (!salesId) {
      setError("No sales ID provided.");
      setPayments([]);
      setRemainingBalance(null);
      setBackendNextDue(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const paymentsResp = await getPaymentsBySalesIdDesc(salesId);
      setPayments(paymentsResp?.data ?? []);

      const dueResp = await getNextPaymentDueForSale(salesId);
      setBackendNextDue(dueResp?.data ?? null);
    } catch (err) {
      console.error("Error fetching payments/next due:", err);
      setError("Failed to load payments. Please try again.");
      setPayments([]);
      setBackendNextDue(null);
    } finally {
      setLoading(false);
    }

    if (rowData && rowData.outstandingBalance != null) {
      setRemainingBalance(rowData.outstandingBalance);
    } else {
      try {
        const remResp = await getRemainingBalanceForSale(salesId);
        setRemainingBalance(remResp?.data ?? null);
      } catch (err) {
        console.debug("Could not fetch remaining balance:", err);
      }
    }
  }, [salesId, rowData]);

  useEffect(() => {
    if (!open) {
      setCurrentTab(0);
      return;
    }
    fetchPaymentsAndBalance();
  }, [open, salesId, rowData, fetchPaymentsAndBalance]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handlePaymentLogged = () => {
    fetchPaymentsAndBalance().catch((err) =>
      console.debug("refresh after log failed", err),
    );
    setCurrentTab(0);
  };

  const formatAmount = (amt) =>
    amt == null ? "N/A" : Number(amt).toLocaleString();

  const formatDate = (raw) => {
    if (!raw) return "N/A";
    try {
      return new Date(raw).toLocaleDateString();
    } catch {
      return String(raw);
    }
  };

  const nextDueDate = useMemo(() => {
    if (backendNextDue) {
      const d = new Date(backendNextDue);
      return isNaN(d) ? null : d;
    }

    if (rowData && rowData.nextPaymentDue) {
      const d = new Date(rowData.nextPaymentDue);
      return isNaN(d) ? null : d;
    }

    const today = stripTime(new Date());
    const unpaidDueDates = payments
      .filter((p) => p.status == null || p.status !== "PAID")
      .map((p) => p.dueDate)
      .filter(Boolean)
      .map((raw) => {
        const d = new Date(raw);
        return isNaN(d) ? null : stripTime(d);
      })
      .filter((d) => d && d >= today);

    if (unpaidDueDates.length > 0) {
      unpaidDueDates.sort((a, b) => a - b);
      return unpaidDueDates[0];
    }

    return null;
  }, [backendNextDue, rowData, payments]);

  function stripTime(dt) {
    if (!(dt instanceof Date)) dt = new Date(dt);
    return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Transition}
      keepMounted
      PaperProps={{
        sx: {
          backgroundColor:
            theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
          backgroundImage: "none",
          borderRadius: "16px",
          boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
          border: `1px solid ${
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.05)"
              : "rgba(0,0,0,0.05)"
          }`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pt: 3,
          pb: 2,
          px: { xs: 2, sm: 3 },
        }}
      >
        <Typography variant="h4" fontWeight="800" color={colors.gray[100]}>
          Payments for Sales ID: #TRX-{salesId}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: colors.gray[400],
            transition: "all 0.2s ease",
            "&:hover": {
              color: colors.redAccent[500],
              transform: "rotate(90deg)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider
        sx={{
          borderColor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.1)"
              : "rgba(0,0,0,0.08)",
        }}
      />

      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Outstanding balance */}
        <Box
          mb={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={2}
          sx={{
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.02)"
                : "rgba(0,0,0,0.02)",
            borderRadius: "12px",
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight="600"
            color={colors.gray[300]}
          >
            Outstanding Balance:
          </Typography>
          <Typography
            variant="h5"
            fontWeight="800"
            color={
              remainingBalance > 0
                ? colors.redAccent[500]
                : colors.greenAccent[500]
            }
          >
            {remainingBalance == null
              ? "—"
              : `₦${formatAmount(remainingBalance)}`}
          </Typography>
        </Box>

        {/* Next Due Date */}
        {nextDueDate && (
          <Box
            mb={3}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            p={2}
            sx={{
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.02)"
                  : "rgba(0,0,0,0.02)",
              borderRadius: "12px",
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight="600"
              color={colors.gray[300]}
            >
              Next Due Date:
            </Typography>
            <Typography
              variant="h5"
              fontWeight="800"
              color={colors.blueAccent[500]}
            >
              {formatDate(nextDueDate)}
            </Typography>
          </Box>
        )}

        {/* Tabbed Interface */}
        <Box
          sx={{
            borderBottom: 1,
            borderColor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.08)",
            mb: 3,
          }}
        >
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: "700",
                fontSize: "0.95rem",
                color: colors.gray[400],
                transition: "color 0.2s ease",
                "&.Mui-selected": {
                  color: colors.blueAccent[500],
                },
              },
              "& .MuiTabs-indicator": {
                backgroundColor: colors.blueAccent[500],
                height: 3,
                borderTopLeftRadius: 3,
                borderTopRightRadius: 3,
              },
            }}
          >
            <Tab label="Payment History" />
            <Tab label="Log Payment" />
          </Tabs>
        </Box>

        {/* Tab Content 1: Payment History */}
        {currentTab === 0 && (
          <Box sx={{ animation: "fadeIn 0.3s ease-out" }}>
            {loading ? (
              <Box textAlign="center" my={4}>
                <CircularProgress sx={{ color: colors.blueAccent[500] }} />
                <Typography variant="body2" mt={2} color={colors.gray[400]}>
                  Loading payments...
                </Typography>
              </Box>
            ) : error ? (
              <Typography
                color={colors.redAccent[500]}
                textAlign="center"
                my={3}
                fontWeight="600"
              >
                {error}
              </Typography>
            ) : payments.length === 0 ? (
              <Box
                textAlign="center"
                my={3}
                p={3}
                borderRadius="12px"
                sx={{
                  border: `1px dashed ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
                }}
              >
                <Typography color={colors.gray[400]} fontWeight="500">
                  No payments found for this sales record.
                </Typography>
              </Box>
            ) : (
              payments.map((p) => (
                <Box
                  key={
                    p.id ?? `${p.salesId}-${p.installmentNo ?? Math.random()}`
                  }
                  mb={2}
                  p={2.5}
                  borderRadius="12px"
                  sx={{
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.03)"
                        : "#f8fafc",
                    border: `1px solid ${
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(0,0,0,0.08)"
                    }`,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.06)"
                          : "#f1f5f9",
                      transform: "translateY(-2px)",
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? "0 4px 12px rgba(0,0,0,0.2)"
                          : "0 4px 12px rgba(0,0,0,0.05)",
                    },
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                    flexWrap="wrap"
                    gap={1}
                  >
                    <Typography
                      fontWeight="800"
                      color={colors.gray[100]}
                      fontSize="1.15rem"
                    >
                      ₦{formatAmount(p.amount)}
                    </Typography>
                    {p.installmentNo != null && (
                      <Chip
                        label={`Installment #${p.installmentNo}`}
                        size="small"
                        sx={{
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(99, 102, 241, 0.15)"
                              : "rgba(79, 70, 229, 0.1)",
                          color: colors.blueAccent[500],
                          fontWeight: "700",
                          fontSize: "0.75rem",
                        }}
                      />
                    )}
                  </Box>

                  <Box
                    display="grid"
                    gridTemplateColumns="repeat(auto-fit, minmax(120px, 1fr))"
                    gap="16px"
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        color={colors.gray[400]}
                        fontWeight="600"
                        textTransform="uppercase"
                        letterSpacing="0.5px"
                      >
                        Method
                      </Typography>
                      <Typography
                        color={colors.gray[100]}
                        fontWeight="600"
                        mt="2px"
                      >
                        {p.method ? p.method.replace(/_/g, " ") : "N/A"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        color={colors.gray[400]}
                        fontWeight="600"
                        textTransform="uppercase"
                        letterSpacing="0.5px"
                      >
                        Date Paid
                      </Typography>
                      <Typography
                        color={
                          p.paidAt ? colors.greenAccent[500] : colors.gray[100]
                        }
                        fontWeight="600"
                        mt="2px"
                      >
                        {p.paidAt ? formatDate(p.paidAt) : "Not Paid"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        color={colors.gray[400]}
                        fontWeight="600"
                        textTransform="uppercase"
                        letterSpacing="0.5px"
                      >
                        Due Date
                      </Typography>
                      <Typography
                        color={colors.gray[100]}
                        fontWeight="500"
                        mt="2px"
                      >
                        {p.dueDate ? formatDate(p.dueDate) : "N/A"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        color={colors.gray[400]}
                        fontWeight="600"
                        textTransform="uppercase"
                        letterSpacing="0.5px"
                      >
                        Receipt No.
                      </Typography>
                      <Typography
                        color={colors.gray[100]}
                        fontWeight="500"
                        mt="2px"
                        sx={{ wordBreak: "break-all" }}
                      >
                        {p.receiptNumber || "N/A"}
                      </Typography>
                    </Box>
                  </Box>

                  {p.notes && (
                    <Box
                      mt={2}
                      pt={1.5}
                      borderTop={`1px dashed ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`}
                    >
                      <Typography
                        variant="caption"
                        color={colors.gray[400]}
                        fontWeight="600"
                        textTransform="uppercase"
                        letterSpacing="0.5px"
                      >
                        Notes
                      </Typography>
                      <Typography
                        color={colors.gray[300]}
                        variant="body2"
                        mt="4px"
                        sx={{ wordBreak: "break-word" }}
                      >
                        {p.notes === "Auto-created from Sales.amountPaid"
                          ? "No notes taken."
                          : p.notes}
                      </Typography>
                    </Box>
                  )}
                </Box>
              ))
            )}
          </Box>
        )}

        {/* Tab Content 2: Log Payment */}
        {currentTab === 1 && (
          <Box sx={{ animation: "fadeIn 0.3s ease-out" }}>
            <LogPayments
              salesId={salesId}
              remainingBalance={remainingBalance}
              onPaymentLogged={handlePaymentLogged}
            />
          </Box>
        )}
      </DialogContent>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(5px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </Dialog>
  );
};

export default PaymentsDialog;
